/**
 * RitualCustomization - Allow users to customise their ritual routines
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Settings2, Sunrise, Moon, Clock, Bell, 
  GripVertical, Plus, Trash2, Check
} from 'lucide-react';
import { DEFAULT_MORNING_ACTIVITIES, DEFAULT_EVENING_ACTIVITIES, RoutineType } from './types';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface RitualCustomizationProps {
  className?: string;
}

export const RitualCustomization: React.FC<RitualCustomizationProps> = ({ className }) => {
  const [activeTab, setActiveTab] = useState<RoutineType>('morning');
  const [morningEnabled, setMorningEnabled] = useState(true);
  const [eveningEnabled, setEveningEnabled] = useState(true);
  const { toast } = useToast();

  const activities = activeTab === 'morning' 
    ? DEFAULT_MORNING_ACTIVITIES 
    : DEFAULT_EVENING_ACTIVITIES;

  const handleSave = () => {
    toast({
      title: "Preferences saved",
      description: "Your habit settings have been updated.",
    });
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-primary" />
          <CardTitle className="text-base">Customise Habits</CardTitle>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Routine toggles */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/10">
            <div className="flex items-center gap-2">
              <Sunrise className="w-4 h-4 text-secondary" />
              <span className="text-sm font-medium">Morning Habit</span>
            </div>
            <Switch 
              checked={morningEnabled} 
              onCheckedChange={setMorningEnabled}
            />
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10">
            <div className="flex items-center gap-2">
              <Moon className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Evening Habit</span>
            </div>
            <Switch 
              checked={eveningEnabled} 
              onCheckedChange={setEveningEnabled}
            />
          </div>
        </div>

        {/* Tab selector */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'morning' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('morning')}
            className="flex-1 gap-1"
          >
            <Sunrise className="w-4 h-4" />
            Morning
          </Button>
          <Button
            variant={activeTab === 'evening' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('evening')}
            className="flex-1 gap-1"
          >
            <Moon className="w-4 h-4" />
            Evening
          </Button>
        </div>

        {/* Activities list */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Activities in your {activeTab} routine:
          </p>
          
          {activities.map((activity, index) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border",
                "bg-card hover:bg-muted/30 transition-colors"
              )}
            >
              <GripVertical className="w-4 h-4 text-muted-foreground/50 cursor-grab" />
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{activity.name}</p>
                <p className="text-xs text-muted-foreground">{activity.duration_minutes} min</p>
              </div>
              
              <Badge variant="secondary" className="text-xs">
                {activity.duration_minutes}m
              </Badge>
              
              <Switch defaultChecked />
            </motion.div>
          ))}
        </div>

        {/* Add custom activity */}
        <Button variant="outline" className="w-full gap-2" size="sm">
          <Plus className="w-4 h-4" />
          Add Custom Activity
        </Button>

        {/* Reminder settings */}
        <div className="pt-2 border-t border-border/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">Daily Reminders</span>
            </div>
            <Switch defaultChecked />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Get reminded at 7am and 9pm
          </p>
        </div>

        <Button onClick={handleSave} className="w-full gap-2">
          <Check className="w-4 h-4" />
          Save Preferences
        </Button>
      </CardContent>
    </Card>
  );
};
